/**
 * Money, as the rules require it written and as the services publish it.
 *
 * `CLAUDE.md` section 2: _"Money is always shown with the currency code and full minor units (for
 * example, KES 1,184,000.00)."_ So `KES 1,184,000.00` and never `KES 1.18M`, never `1,184,000`, and
 * never a number without its code.
 *
 * Two things are never done here, and both are platform rules rather than preferences:
 *
 *   - **Minor units are never divided into a float.** `AGENTS.md`: never use floating-point values
 *     for money. The integer is split by string arithmetic, so 4_499_999_999_999 formats exactly
 *     where `/ 100` would already have lost it.
 *   - **An amount is never shown without the currency it arrived with.** The two travel together in
 *     every published payload on this platform, and separating them is how a courier reads a TZS
 *     figure as a KES one.
 *
 * ## The one place this disagrees with the design, and why the rules win
 *
 * Both driver design files write money with no decimal places: `TZS 9,600 today`, `TZS 48,200`,
 * `You earn TZS 2,400`, `Commission (FIN-06) · – TZS 1,800`. `CLAUDE.md` section 2 asks for full
 * minor units. Those two cannot both be satisfied, and section 2 is a platform rule about money
 * where the design's shorter form is a sample value, so the rule is followed and the difference is
 * recorded as Q-62 rather than decided silently. It is also, today, almost never visible: nothing
 * on this platform publishes what a courier has earned, so every money figure on these screens is
 * an absence rather than a number. The one path that does produce one is a caller naming a
 * `payoutId`, which FIN-07 answers with `amountMinor` and `currency`.
 */

/**
 * How many minor units make one major unit, per currency.
 *
 * ISO 4217 exponents, and only for the currencies this platform's own contracts and fixtures name.
 * An unknown code falls back to two, which is the exponent of all but about twenty of the world's
 * currencies — and the fallback is reported by `minorUnitsAreAssumed` rather than hidden, because a
 * JPY amount formatted with two decimals is wrong by a hundredfold and a courier would not know.
 */
const MINOR_UNIT_EXPONENTS: Readonly<Record<string, number>> = {
  // East Africa, which is where this product operates.
  KES: 2,
  TZS: 2,
  UGX: 0,
  RWF: 0,
  ETB: 2,
  // The settlement currencies the finance services name.
  USD: 2,
  EUR: 2,
  GBP: 2,
  // Zero-decimal currencies that are commonly got wrong.
  JPY: 0,
  KRW: 0,
  VND: 0,
  ISK: 0,
  CLP: 0,
  XOF: 0,
  XAF: 0,
  // Three-decimal currencies, the other direction of the same mistake.
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] ?? 2;
}

/** True when the exponent above was a fallback rather than a known one. */
export function minorUnitsAreAssumed(currency: string): boolean {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] === undefined;
}

/**
 * `KES 1,184,000.00`, from an integer number of minor units and a currency code.
 *
 * Grouped in threes with a comma and separated by a full stop, which is what the design writes and
 * what the rule's own example writes. Negative amounts take the design's own form —
 * `– TZS 1,800` in `15 WP14`'s commission row — with an en dash and a space, which is the glyph the
 * design uses rather than a hyphen.
 */
export function formatMoney(amountMinor: number, currency: string): string {
  if (!Number.isInteger(amountMinor)) {
    throw new TypeError(`money must be whole minor units, got ${String(amountMinor)}`);
  }
  const code = currency.toUpperCase();
  const exponent = minorUnitExponent(code);
  const negative = amountMinor < 0;
  const digits = String(Math.abs(amountMinor)).padStart(exponent + 1, "0");
  const major = digits.slice(0, digits.length - exponent);
  const minor = exponent === 0 ? "" : `.${digits.slice(digits.length - exponent)}`;
  const grouped = major.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "– " : ""}${code} ${grouped}${minor}`;
}
